import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, Mail, Monitor, RotateCcw, Smartphone } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { promptForPush } from "@/lib/onesignal";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";

export interface NotificationPref {
  type: string;
  category: string;
  label: string;
  description: string;
  inApp: boolean;
  push: boolean;
  email: boolean;
  required?: boolean;
}

interface CategoryGroup {
  category: string;
  types: Array<{
    key: string;
    config: {
      label: string;
      description?: string;
      defaults: { inApp: boolean; push: boolean; email: boolean };
      required?: boolean;
    };
  }>;
}

const CATEGORY_LABELS: Record<string, string> = {
  leave: "Leave & Attendance",
  work_location: "Work Location",
  tasks: "Tasks & Projects",
  effort: "Effort Tracking",
  celebrations: "Celebrations",
  timesheet: "Timesheet",
  system: "System",
};

interface NotificationPreferencesPanelProps {
  showCard?: boolean;
  onClose?: () => void;
}

export default function NotificationPreferencesPanel({
  showCard = true,
  onClose,
}: NotificationPreferencesPanelProps) {
  const [localPrefs, setLocalPrefs] = useState<NotificationPref[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: preferences, isLoading } = useQuery<NotificationPref[]>({
    queryKey: ["/api/notification-preferences"],
  });

  const { data: registry } = useQuery<CategoryGroup[]>({
    queryKey: ["/api/notification-registry"],
  });

  useEffect(() => {
    if (preferences) {
      setLocalPrefs(preferences);
      setHasChanges(false);
    }
  }, [preferences]);

  const saveMutation = useMutation({
    mutationFn: (prefs: Array<{ type: string; inApp: boolean; push: boolean; email: boolean }>) =>
      apiRequest("PUT", "/api/notification-preferences", prefs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notification-preferences"] });
      setHasChanges(false);
      toast({ title: "Preferences saved", description: "Your notification preferences have been updated." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to save preferences.", variant: "destructive" });
    },
  });

  const grouped = useMemo(() => {
    const map = new Map<string, NotificationPref[]>();
    for (const pref of localPrefs) {
      if (!map.has(pref.category)) {
        map.set(pref.category, []);
      }
      map.get(pref.category)!.push(pref);
    }
    return Array.from(map.entries());
  }, [localPrefs]);

  const handleToggle = (type: string, channel: "inApp" | "push" | "email", value: boolean) => {
    setLocalPrefs((prev) =>
      prev.map((pref) => {
        if (pref.type !== type) {
          return pref;
        }
        if (channel === "email" && pref.required) {
          return pref;
        }
        return { ...pref, [channel]: value };
      })
    );
    setHasChanges(true);
  };

  const handleCategoryEmailToggle = (category: string, value: boolean) => {
    setLocalPrefs((prev) =>
      prev.map((pref) => (
        pref.category === category && !pref.required
          ? { ...pref, email: value }
          : pref
      ))
    );
    setHasChanges(true);
  };

  const handleResetDefaults = () => {
    if (!registry) {
      return;
    }

    const configByType = new Map(
      registry.flatMap((group) => group.types.map((type) => [type.key, type.config]))
    );

    setLocalPrefs((prev) =>
      prev.map((pref) => {
        const config = configByType.get(pref.type);
        if (!config) {
          return pref;
        }

        return {
          ...pref,
          inApp: config.defaults.inApp,
          push: config.defaults.push,
          email: config.required ? true : config.defaults.email,
          required: config.required === true,
        };
      })
    );
    setHasChanges(true);
  };

  const handleSave = () => {
    saveMutation.mutate(
      localPrefs.map((pref) => ({
        type: pref.type,
        inApp: pref.inApp,
        push: pref.push,
        email: pref.email,
      }))
    );
  };

  const handleEnablePush = async () => {
    try {
      await promptForPush();
      toast({ title: "Push notifications enabled", description: "You'll now receive browser push notifications." });
    } catch {
      toast({
        title: "Push not enabled",
        description: "Please allow notifications in your browser settings.",
        variant: "destructive",
      });
    }
  };

  const content = (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Button variant="outline" size="sm" onClick={handleEnablePush}>
          <Smartphone className="mr-2 h-4 w-4" />
          Enable Push Notifications
        </Button>
        <Button variant="ghost" size="sm" onClick={handleResetDefaults}>
          <RotateCcw className="mr-2 h-4 w-4" />
          Reset to Defaults
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-6">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="space-y-3">
              <Skeleton className="h-5 w-40" />
              {Array.from({ length: 3 }).map((__, rowIndex) => (
                <div key={rowIndex} className="flex items-center gap-4 rounded-lg border border-border p-3">
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-52" />
                    <Skeleton className="h-3 w-72" />
                  </div>
                  <Skeleton className="h-5 w-10 rounded-full" />
                  <Skeleton className="h-5 w-10 rounded-full" />
                  <Skeleton className="h-5 w-10 rounded-full" />
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-8">
          <div className="hidden items-center gap-3 px-3 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground md:flex">
            <div className="flex-1">Notification Type</div>
            <div className="w-16 text-center">
              <Monitor className="mx-auto mb-1 h-3.5 w-3.5" />
              In-App
            </div>
            <div className="w-16 text-center">
              <Smartphone className="mx-auto mb-1 h-3.5 w-3.5" />
              Push
            </div>
            <div className="w-16 text-center">
              <Mail className="mx-auto mb-1 h-3.5 w-3.5" />
              Email
            </div>
          </div>

          {grouped.map(([category, prefs]) => {
            const emailToggleablePrefs = prefs.filter((pref) => !pref.required);
            const categoryEmailEnabled =
              emailToggleablePrefs.length > 0 && emailToggleablePrefs.every((pref) => pref.email);

            return (
              <div key={category} className="space-y-3">
                <div className="flex flex-col gap-3 border-b border-border pb-2 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="text-sm font-semibold text-foreground">
                    {CATEGORY_LABELS[category] || category}
                  </h3>
                  {emailToggleablePrefs.length > 0 && (
                    <div className="flex items-center gap-3 rounded-lg border border-border px-3 py-2">
                      <span className="text-xs font-medium text-muted-foreground">Mute all email in this category</span>
                      <Switch
                        checked={categoryEmailEnabled}
                        onCheckedChange={(value) => handleCategoryEmailToggle(category, value)}
                      />
                    </div>
                  )}
                </div>

                {prefs.map((pref) => (
                  <div
                    key={pref.type}
                    className="grid gap-4 rounded-xl border border-border bg-card p-4 md:grid-cols-[minmax(0,1fr)_64px_64px_64px] md:items-center"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-foreground">{pref.label}</p>
                        {pref.required && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground" title="Email delivery for this notification type is required.">
                            <Lock className="h-3 w-3" />
                            Required
                          </span>
                        )}
                      </div>
                      {pref.description && <p className="mt-1 text-sm text-muted-foreground">{pref.description}</p>}
                    </div>
                    <div className="flex items-center justify-between gap-2 md:block">
                      <span className="text-xs font-medium text-muted-foreground md:hidden">In-App</span>
                      <div className="flex justify-center">
                        <Switch checked={pref.inApp} onCheckedChange={(value) => handleToggle(pref.type, "inApp", value)} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 md:block">
                      <span className="text-xs font-medium text-muted-foreground md:hidden">Push</span>
                      <div className="flex justify-center">
                        <Switch checked={pref.push} onCheckedChange={(value) => handleToggle(pref.type, "push", value)} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 md:block">
                      <span className="text-xs font-medium text-muted-foreground md:hidden">Email</span>
                      <div className="flex justify-center">
                        {pref.required ? (
                          <div className="flex h-9 items-center justify-center text-muted-foreground" title="Email delivery for this notification type is required.">
                            <Lock className="h-4 w-4" />
                          </div>
                        ) : (
                          <Switch checked={pref.email} onCheckedChange={(value) => handleToggle(pref.type, "email", value)} />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}

          <div className="flex justify-end gap-2 border-t border-border pt-4">
            {onClose && (
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
            )}
            <Button onClick={handleSave} disabled={!hasChanges || saveMutation.isPending}>
              {saveMutation.isPending ? "Saving..." : "Save Preferences"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  if (!showCard) {
    return content;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notification Preferences</CardTitle>
        <CardDescription>Choose how you want to be notified for different events.</CardDescription>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}
